import { useMemo, useState } from 'react';
import { SmileIcon, XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
    LUCIDE_ICON_CATALOG,
    LUCIDE_ICON_GROUPS,
    filterLucideIcons,
    findLucideMenuIcon,
} from '@/lib/lucideMenuIcons';

export { findLucideMenuIcon } from '@/lib/lucideMenuIcons';

/**
 * Seletor Lucide para o painel do clube — botão ao lado do `children`, grid inline abaixo.
 */
export default function SelectIconClub({ value, name, onChange, className, children }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [groupId, setGroupId] = useState('all');

    const selected = useMemo(() => findLucideMenuIcon(value), [value]);
    const SelectedIcon = selected?.Component;

    const filtered = useMemo(
        () => filterLucideIcons(LUCIDE_ICON_CATALOG, search, groupId),
        [search, groupId],
    );

    const close = () => {
        setOpen(false);
        setSearch('');
        setGroupId('all');
    };

    const select = item => {
        onChange?.(name, item.value);
        close();
    };

    const clear = event => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        onChange?.(name, '');
        close();
    };

    return (
        <div className={cn('space-y-2', className)}>
            <div className="flex items-end gap-2">
                <div className="flex shrink-0 items-center gap-1 pb-0.5">
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="size-10 shrink-0"
                        title={selected ? selected.label : 'Selecionar ícone'}
                        aria-label={selected ? selected.label : 'Selecionar ícone'}
                        aria-expanded={open}
                        onClick={() => (open ? close() : setOpen(true))}
                    >
                        {SelectedIcon ? (
                            <SelectedIcon className="size-5" />
                        ) : (
                            <SmileIcon className="size-5 text-muted-foreground" />
                        )}
                    </Button>

                    {selected ? (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Remover ícone"
                            title="Remover ícone"
                            onClick={clear}
                        >
                            <XIcon className="size-4" />
                        </Button>
                    ) : null}
                </div>

                {children ? <div className="min-w-0 flex-1">{children}</div> : null}
            </div>

            {open ? (
                <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
                    <Input
                        placeholder="Buscar…"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        autoFocus
                    />

                    <div className="flex flex-wrap gap-1">
                        <Button
                            type="button"
                            size="sm"
                            variant={groupId === 'all' ? 'default' : 'outline'}
                            className="h-7 px-2 text-xs"
                            onClick={() => setGroupId('all')}
                        >
                            Todos
                        </Button>
                        {LUCIDE_ICON_GROUPS.map(group => (
                            <Button
                                key={group.id}
                                type="button"
                                size="sm"
                                variant={groupId === group.id ? 'default' : 'outline'}
                                className="h-7 px-2 text-xs"
                                onClick={() => setGroupId(group.id)}
                            >
                                {group.label}
                            </Button>
                        ))}
                    </div>

                    <div className="grid max-h-48 grid-cols-4 gap-1.5 overflow-y-auto sm:grid-cols-6">
                        {filtered.map(item => {
                            const Icon = item.Component;
                            const isActive = selected?.value === item.value;
                            return (
                                <button
                                    key={item.value}
                                    type="button"
                                    title={item.label}
                                    onClick={() => select(item)}
                                    className={cn(
                                        'flex aspect-square items-center justify-center rounded-lg border p-2 transition-colors hover:bg-muted',
                                        isActive && 'border-primary bg-primary/5 ring-1 ring-primary/30',
                                    )}
                                >
                                    <Icon className="size-5" />
                                </button>
                            );
                        })}
                        {filtered.length === 0 ? (
                            <p className="col-span-full py-6 text-center text-sm text-muted-foreground">
                                Nenhum ícone encontrado.
                            </p>
                        ) : null}
                    </div>

                    <Button type="button" variant="ghost" size="sm" onClick={clear}>
                        Sem ícone
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
